import { showError } from '@/utils/common';
import { action, makeObservable, observable } from 'mobx';

import type { ApiResponse, WrappedAxiosItemsResult, WrappedItemsResult } from '@/api/types';
import type { AxiosPromise } from 'axios';

interface IStorage<T> {
  data: T[];
  count: number;

  get(): T[];
  set(data): void;
  append(item): void;
  update(item): void;
  delete(id): void;
}

export class SimpleStoreStorage<T extends { id: number }> implements IStorage<T> {
  @observable data: T[] | null = null;
  @observable count: number;

  constructor() {
    makeObservable(this);
  }

  get() {
    return this.data;
  }

  @action
  set(data) {
    this.data = data;
  }

  @action
  append(item) {
    this.data.push(item);
  }

  @action
  update(item: T) {
    const index = this.data.findIndex(({ id }: T) => id === item.id);

    if (index !== -1) {
      this.data[index] = item;
    }
  }

  @action
  delete(itemId: number) {
    this.data = this.data.filter(({ id }: T) => id !== itemId);
  }
}

export class CachedStorage<T extends { id: number }> extends SimpleStoreStorage<T> {
  private readonly factorInSeconds: number = 1000;
  private lastUpdate: number = 0;

  // todo change default life time when will fix the store (10 min)
  constructor(private readonly lifetimeSeconds: number = 10) {
    super();
  }

  expired(): boolean {
    return Date.now() - this.lastUpdate > this.lifetimeSeconds * this.factorInSeconds;
  }

  get() {
    if (this.expired() || !this.data) {
      this.lastUpdate = Date.now();
      return null;
    }
    return this.data;
  }
}

interface IStoreDataLoader<P> {
  loading: boolean;
  load(params?: P);
}

export class DummyDataLoader<P> implements IStoreDataLoader<P> {
  @observable loading: boolean = false;

  constructor() {
    makeObservable(this);
  }

  load(params?: P) {
    return Promise.resolve([]);
  }
}

export class StoreDataLoader<T, P> implements IStoreDataLoader<P> {
  @observable loading: boolean = false;

  constructor(private readonly loaderFn: (params: P) => ApiResponse<WrappedAxiosItemsResult<T[]>>) {
    makeObservable(this);
  }

  load(params?: P): AxiosPromise<WrappedAxiosItemsResult<T[]>> {
    return this.loaderFn(params).source;
  }
}

export class CollectionStore<T extends { id: number }, P> {
  constructor(
    private readonly dataLoader: IStoreDataLoader<P>,
    private readonly storage: IStorage<T>,
  ) {}

  set loading(loading: boolean) {
    this.dataLoader.loading = loading;
  }

  get loading(): boolean {
    return this.dataLoader.loading;
  }

  get items(): T[] {
    return this.storage.data || [];
  }

  get count(): number {
    return this.storage.count;
  }

  fillStore(items: T[], count?: number): T[] {
    this.storage.set(items);
    this.storage.count = count || items.length;

    return items;
  }

  addItem(item: T): void {
    this.storage.append(item);
  }

  updateItem(item: T): void {
    this.storage.update(item);
  }

  deleteItem(itemId: number): void {
    this.storage.delete(itemId);
  }

  forceLoad(params?: P): Promise<T[]> {
    this.loading = true;
    return this.dataLoader
      .load(params)
      .then(({ data }: WrappedItemsResult<T[]>) => this.fillStore(data.items, data.count))
      .catch(showError)
      .finally(() => (this.loading = false));
  }

  ensureData(params?: P): Promise<T[]> {
    const result = this.storage.get();
    if (result === null) {
      return this.forceLoad(params);
    }
    return Promise.resolve(result);
  }

  asyncItems(params?: P): Promise<T[]> {
    return this.ensureData(params);
  }
}
